(() => {
  const LEAD_STORAGE_PREFIX = "klyyr-lead:";
  const FORMS_SUCCESS_SELECTOR = [
    "[data-shopify-forms-success]",
    ".shopify-form--success",
    ".shopify-forms-success",
    "[data-form-status='success']",
  ].join(",");

  /**
   * @returns {{ content_group: string, page_type: string }}
   */
  function pageContext() {
    return {
      content_group: document.body?.dataset?.analyticsContentGroup || "other",
      page_type: document.body?.dataset?.analyticsPageType || "",
    };
  }

  /**
   * @param {string} eventName
   * @param {Record<string, string | number | boolean | undefined>} [payload]
   */
  function publish(eventName, payload) {
    const data = { ...pageContext(), ...payload };
    Object.keys(data).forEach((key) => {
      if (data[key] == null || data[key] === "") delete data[key];
    });

    const send = () => {
      if (typeof window.Shopify?.analytics?.publish === "function") {
        window.Shopify.analytics.publish(eventName, data);
        return true;
      }
      return false;
    };

    if (send()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (send() || attempts > 40) window.clearInterval(timer);
    }, 250);
  }

  /**
   * @param {string} leadType
   * @returns {boolean}
   */
  function markLeadOnce(leadType) {
    const key = `${LEAD_STORAGE_PREFIX}${leadType}:${window.location.pathname}`;
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, "1");
    } catch {
      // Private mode can block sessionStorage; still publish.
    }
    return true;
  }

  /**
   * @param {string} leadType
   */
  function publishLead(leadType) {
    if (!leadType || !markLeadOnce(leadType)) return;
    publish("generate_lead", { lead_type: leadType });
  }

  /**
   * @param {Element | null} root
   * @returns {string}
   */
  function leadTypeFrom(root) {
    if (!(root instanceof HTMLElement)) return "";
    if (root.dataset.analyticsLeadType) return root.dataset.analyticsLeadType;
    const host = root.closest("[data-analytics-lead-type]");
    return host instanceof HTMLElement ? host.dataset.analyticsLeadType || "" : "";
  }

  function bindPurchaseOptions(scope) {
    scope.querySelectorAll("[data-analytics-purchase-option]").forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.dataset.analyticsBound === "true") return;
      input.dataset.analyticsBound = "true";
      input.addEventListener("change", () => {
        if (!input.checked) return;
        publish("purchase_option_selected", {
          purchase_type: input.dataset.analyticsPurchaseOption || "",
        });
      });
    });
  }

  function bindFaqs(scope) {
    scope.querySelectorAll("[data-analytics-faq]").forEach((details) => {
      if (!(details instanceof HTMLDetailsElement) || details.dataset.analyticsBound === "true") return;
      details.dataset.analyticsBound = "true";
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        const question = details.querySelector(".pdp-faq__q");
        const itemId = question?.textContent?.trim() || "";
        publish("select_content", {
          content_type: "faq",
          item_id: itemId,
        });
      });
    });
  }

  function bindCtas(scope) {
    scope.querySelectorAll("[data-analytics-cta]").forEach((el) => {
      if (!(el instanceof HTMLElement) || el.dataset.analyticsBound === "true") return;
      el.dataset.analyticsBound = "true";
      el.addEventListener("click", () => {
        publish("cta_click", {
          cta_id: el.dataset.analyticsCta || "",
          cta_location: el.dataset.analyticsCtaLocation || "",
        });
      });
    });
  }

  function bindPostedLeads(scope) {
    scope.querySelectorAll("[data-analytics-lead-posted]").forEach((el) => {
      if (!(el instanceof HTMLElement) || el.dataset.analyticsBound === "true") return;
      el.dataset.analyticsBound = "true";
      publishLead(leadTypeFrom(el));
    });
  }

  /**
   * @param {Element} host
   */
  function observeFormsHost(host) {
    if (!(host instanceof HTMLElement) || host.dataset.analyticsFormsObserved === "true") return;
    host.dataset.analyticsFormsObserved = "true";

    const maybeSuccess = () => {
      if (host.querySelector(FORMS_SUCCESS_SELECTOR)) {
        publishLead(host.dataset.analyticsLeadType || "");
      }
    };

    maybeSuccess();
    const observer = new MutationObserver(maybeSuccess);
    observer.observe(host, { childList: true, subtree: true, attributes: true });
  }

  function bindFormsHosts(scope) {
    scope.querySelectorAll("[data-analytics-lead-type]").forEach((host) => {
      observeFormsHost(host);
    });
  }

  /**
   * @param {ParentNode} [scope]
   */
  function init(scope = document) {
    bindPurchaseOptions(scope);
    bindFaqs(scope);
    bindCtas(scope);
    bindPostedLeads(scope);
    bindFormsHosts(scope);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof Element) init(event.target);
  });
})();
